import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, MinusCircleOutlined, PlusOutlined, RobotOutlined } from '@ant-design/icons';
import { gql, useQuery } from '@apollo/client';
import {
  Button,
  Collapse,
  DatePicker,
  Divider,
  Input,
  Radio,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { OnboardingTaskRoles, TaskInputTypes } from '../constants';
import SelectContact from '../../contacts/SelectContact';
import CommitmentFormAction from './CommitmentFormAction';
import TrainerAssessmentAction from './TrainerAssessmentAction';

const contactNameQuery = gql`
  query WhoTrainedContactName($_id: ID!) {
    contactForAdmin(_id: $_id) {
      _id
      fullName
    }
  }
`;

const { Text } = Typography;

// Tasks with an autoSource are system-managed — set automatically, never edited by hand.
const AUTO_SOURCE_LABEL = {
  CONTACT_EMAIL_AND_PHONE: 'Email & phone',
  STRATEGY_GUIDE_UPLOADED: 'Strategy Guide (uploaded)',
  STRATEGY_GUIDE_APPROVED: 'Strategy Guide',
  COMMITMENT_FORM_CREATED: 'MOU/PCF (created)',
  COMMITMENT_FORM_SENT: 'MOU/PCF (sent)',
  COMMITMENT_FORM_SIGNED: 'MOU/PCF',
  USER_INVITE_SENT: 'User invite',
  USER_ACCOUNT_CREATED: 'User account',
  COORDINATOR_COMMITMENT_FORM_SIGNED: "Coordinator's PCF",
  TRAINER_ASSESSMENT_PASSED: 'Trainer assessment',
};

function TaskResponseItem({
  template,
  response,
  onSubmit,
  onUpdateNotes,
  isReadOnly = false,
  defaultAnswer = 'No',
  contactName,
  contactId,
  partnerType,
  onDataChanged,
}) {
  const [answer, setAnswer] = useState(response?.answer || '');
  const [saving, setSaving] = useState('');
  const [notesValue, setNotesValue] = useState(response?.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);

  const isComplete = !!response?.completedAt;
  const isYesNo = template.inputType === 'YES_NO';
  const isWhoTrained = template.taskName?.toLowerCase().includes('who trained');
  const hasNotes = !!response?.notes;

  const { data: contactNameData } = useQuery(contactNameQuery, {
    variables: { _id: response?.answer },
    skip: !isWhoTrained || !response?.answer,
    fetchPolicy: 'cache-first',
  });
  const whoTrainedName = contactNameData?.contactForAdmin?.fullName;

  const allowNa = !!template.allowNotApplicable;
  // autoSource tasks are system-managed — never manually editable, regardless of caller.
  const autoSource = template.autoSource;
  const isTrainerAssessment = autoSource === 'TRAINER_ASSESSMENT_PASSED';
  const readOnly = isReadOnly || !!autoSource;
  const effectiveAnswer = response?.answer || (isYesNo && !autoSource ? defaultAnswer : '');
  const isNa = effectiveAnswer === 'N/A';
  const statusIcon = autoSource
    ? isComplete
      ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
      : <ClockCircleOutlined style={{ color: '#faad14' }} />
    : isNa
      ? <MinusCircleOutlined style={{ color: '#bfbfbf' }} />
      : isYesNo
        ? effectiveAnswer === 'Yes'
          ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
        : isComplete
          ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
          : <ClockCircleOutlined style={{ color: '#faad14' }} />;

  const handleSubmit = async (val) => {
    const submitValue = val !== undefined ? val : answer;
    if (!submitValue) return;
    setSaving(submitValue);
    await onSubmit(template._id, submitValue);
    setSaving('');
  };

  const handleSaveNotes = async () => {
    if (!onUpdateNotes) return;
    setSavingNotes(true);
    await onUpdateNotes(template._id, notesValue);
    setSavingNotes(false);
  };

  const notesPanel = (
    <div style={{ paddingTop: 8 }}>
      <Input.TextArea
        value={notesValue}
        onChange={(e) => setNotesValue(e.target.value)}
        rows={3}
        placeholder="Add notes..."
      />
      <Button
        size="small"
        type="primary"
        style={{ marginTop: 6 }}
        loading={savingNotes}
        onClick={handleSaveNotes}
        disabled={notesValue === (response?.notes || '')}
      >
        Save Notes
      </Button>
    </div>
  );

  return (
    <div style={{ marginBottom: 16, padding: 12, border: '1px solid #f0f0f0', borderRadius: 6 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Space size={6}>
          {statusIcon}
          <Text strong>{template.taskName}</Text>
          <Tag>{OnboardingTaskRoles[template.assignedRole]?.label || template.assignedRole}</Tag>
          <Tag color="blue">{TaskInputTypes[template.inputType]?.label}</Tag>
          {autoSource && (
            <Tag color="geekblue" icon={<RobotOutlined />}>
              Auto
            </Tag>
          )}
          <Text type="secondary">{template.daysToComplete}d to complete</Text>
        </Space>
        <div style={{ width: 12 }} />
        {readOnly ? (
          <span>
            {autoSource ? (
              <>
                {isTrainerAssessment ? (
                  <>
                    <Radio.Group
                      value={isComplete ? 'Certified' : 'Uncertified'}
                      disabled
                      size="small"
                      style={{ marginRight: 8 }}
                    >
                      <Radio.Button value="Certified">✓ Certified</Radio.Button>
                      <Radio.Button value="Uncertified">
                        ✗ Uncertified
                      </Radio.Button>
                    </Radio.Group>
                    <Text type={isComplete ? 'success' : 'secondary'}>
                      {contactName || 'Trainer'}{' '}
                      {isComplete ? 'is certified' : 'is not yet certified'}
                    </Text>
                  </>
                ) : (
                  <>
                    <Radio.Group
                      value={isComplete ? 'Yes' : 'No'}
                      disabled
                      size="small"
                      style={{ marginRight: 8 }}
                    >
                      <Radio.Button value="Yes">✓ Yes</Radio.Button>
                      <Radio.Button value="No">✗ No</Radio.Button>
                    </Radio.Group>
                    <Text type={isComplete ? 'success' : 'secondary'}>
                      {AUTO_SOURCE_LABEL[autoSource] || 'Item'}{' '}
                      {isComplete ? 'found' : 'not found'} on contact{' '}
                      {contactName || '—'}
                    </Text>
                  </>
                )}
                <Text type="secondary" italic style={{ marginLeft: 8 }}>
                  (set automatically)
                </Text>
                {autoSource === 'COMMITMENT_FORM_SIGNED' && (
                  <CommitmentFormAction
                    contactId={contactId}
                    partnerType={partnerType}
                  />
                )}
                {isTrainerAssessment && (
                  <TrainerAssessmentAction
                    contactId={contactId}
                    contactName={contactName}
                    onSubmitted={onDataChanged}
                  />
                )}
              </>
            ) : (
              <Text type={isComplete ? 'success' : 'secondary'}>
                {isWhoTrained
                  ? (whoTrainedName || response?.answer || '—')
                  : (response?.answer || '—')}
              </Text>
            )}
            {response?.completedAt && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({new Date(response.completedAt).toLocaleDateString()})
              </Text>
            )}
          </span>
        ) : (
          <Space>
          {isYesNo && (
            <Radio.Group
              value={response?.answer || defaultAnswer}
              onChange={(e) => handleSubmit(e.target.value)}
              disabled={!!saving}
            >
              <Radio.Button value="Yes">✓ Yes</Radio.Button>
              <Radio.Button value="No">✗ No</Radio.Button>
              {allowNa && <Radio.Button value="N/A">N/A</Radio.Button>}
            </Radio.Group>
          )}
          {isWhoTrained && !isYesNo && (
            <SelectContact
              value={response?.answer || undefined}
              onChange={(val) => handleSubmit(val)}
              style={{ width: 280 }}
              filters={{ hasMouSigned: true }}
            />
          )}
          {!isYesNo && !isWhoTrained && template.inputType === 'DATE' && (
            <>
              <DatePicker
                value={answer ? dayjs(answer) : dayjs()}
                onChange={(_, s) => setAnswer(s)}
                defaultValue={dayjs()}
              />
              <Button
                type="primary"
                size="small"
                onClick={() => handleSubmit()}
                loading={!!saving}
                disabled={!answer}
              >
                Save
              </Button>
            </>
          )}
          {!isYesNo && !isWhoTrained && (template.inputType === 'TEXT' || template.inputType === 'FORM') && (
            <>
              <Input
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={
                  template.inputType === 'FORM' ? 'Enter form reference or notes' : 'Enter answer'
                }
                style={{ width: 300 }}
              />
              <Button
                type="primary"
                size="small"
                onClick={() => handleSubmit()}
                loading={!!saving}
                disabled={!answer}
              >
                Save
              </Button>
            </>
          )}
          {allowNa && !isYesNo && (
            <Button
              size="small"
              onClick={() => handleSubmit('N/A')}
              loading={saving === 'N/A'}
              disabled={!!saving}
            >
              N/A
            </Button>
          )}
        </Space>
        )}
      </div>

      {hasNotes ? (
        <Collapse
          defaultActiveKey={['notes']}
          ghost
          size="small"
          items={[{ key: 'notes', label: 'Notes', children: notesPanel }]}
        />
      ) : (
        <Collapse
          ghost
          size="small"
          items={[{
            key: 'notes',
            label: (
              <span style={{ color: '#8c8c8c' }}>
                <Divider style={{ margin: '0 8px 0 0', display: 'inline-block', width: 40, verticalAlign: 'middle' }} />
                <PlusOutlined style={{ marginRight: 4 }} />
                Add Notes
              </span>
            ),
            children: notesPanel,
          }]}
        />
      )}
    </div>
  );
}

export default TaskResponseItem;
